import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Save, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { OrderbookAlert } from '@/types'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { TagExpressionInput } from '@/components/common/TagExpressionInput'
import { legacyTagsToExpression } from '@/lib/tagExpression'
import { TelegramDestinationSelect } from '@/components/settings/TelegramDestinationSelect'
import { OrderbookExchangeSelector } from './OrderbookExchangeSelector'

interface OrderbookAlertEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  alert?: OrderbookAlert | null
  onSave: (alert: Partial<OrderbookAlert>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

export function OrderbookAlertEditSheet({
  open,
  onOpenChange,
  alert,
  onSave,
  onDelete,
}: OrderbookAlertEditSheetProps) {
  const isNew = !alert?.id

  const [name, setName] = useState('')
  const [exchanges, setExchanges] = useState<string[]>([])
  const [marketType, setMarketType] = useState<'spot' | 'futures'>('futures')
  const exchangeOptions = useExchangeOptions('orderbook', marketType)
  const unsupportedExchanges = exchanges.filter((exchange) => !exchangeOptions.includes(exchange))
  const [precision, setPrecision] = useState('0.01')
  const [minOrderUsdt, setMinOrderUsdt] = useState([50000])
  const [gracePeriodSeconds, setGracePeriodSeconds] = useState([120])
  const [retriggerIntervalDays, setRetriggerIntervalDays] = useState([2])
  const [retriggerHourUtc, setRetriggerHourUtc] = useState([10])
  const [priceTolerancePct, setPriceTolerancePct] = useState([10])
  const [updateNotificationPct, setUpdateNotificationPct] = useState<number | null>(8)
  const [maxDeviationPct, setMaxDeviationPct] = useState<number | null>(15)
  const [enabled, setEnabled] = useState(true)
  const [ignoreSymbols, setIgnoreSymbols] = useState('')
  const [tagExpression, setTagExpression] = useState('')
  const [legacyRequired, setLegacyRequired] = useState<string[]>([])
  const [legacyExcluded, setLegacyExcluded] = useState<string[]>([])

  // Notification settings - Detection
  const [enableTelegram, setEnableTelegram] = useState(true)
  const [enablePushover, setEnablePushover] = useState(false)
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramReplyTo, setTelegramReplyTo] = useState('')
  const [pushoverGroupKey, setPushoverGroupKey] = useState('')
  const [pushoverNotifyUsers, setPushoverNotifyUsers] = useState('')
  // Notification settings - Removal
  const [enableRemovalNotifications, setEnableRemovalNotifications] = useState(true)
  const [removalTelegramEnabled, setRemovalTelegramEnabled] = useState(true)
  const [removalTelegramChatId, setRemovalTelegramChatId] = useState('')
  const [removalTelegramReplyTo, setRemovalTelegramReplyTo] = useState('')
  const [removalPushoverEnabled, setRemovalPushoverEnabled] = useState(false)
  const [removalPushoverGroupKey, setRemovalPushoverGroupKey] = useState('')
  const [removalPushoverNotifyUsers, setRemovalPushoverNotifyUsers] = useState('')
  // Notification settings - Retrigger
  const [enableRetriggerNotifications, setEnableRetriggerNotifications] = useState(true)
  const [retriggerTelegramEnabled, setRetriggerTelegramEnabled] = useState(true)
  const [retriggerTelegramChatId, setRetriggerTelegramChatId] = useState('')
  const [retriggerTelegramReplyTo, setRetriggerTelegramReplyTo] = useState('')
  const [retriggerPushoverEnabled, setRetriggerPushoverEnabled] = useState(false)
  const [retriggerPushoverGroupKey, setRetriggerPushoverGroupKey] = useState('')
  const [retriggerPushoverNotifyUsers, setRetriggerPushoverNotifyUsers] = useState('')
  // Per-exchange notification overrides
  const [exchangeNotifications, setExchangeNotifications] = useState<Record<string, {
    enabled: boolean
    telegramChatId: string
    telegramReplyTo: string
    pushoverGroupKey: string
    pushoverNotifyUsers: string
  }>>({})
  const [expandedExchanges, setExpandedExchanges] = useState<Set<string>>(new Set())

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditingMinOrder, setIsEditingMinOrder] = useState(false)
  const [minOrderInputValue, setMinOrderInputValue] = useState('')

  useEffect(() => {
    if (alert) {
      setName(alert.name || '')
      setExchanges(alert.exchanges || [])
      setMarketType(alert.market_type || 'futures')
      setPrecision(alert.precision || '0.01')
      // Ensure min order is at least 10000 to fix legacy alerts with 0
      // Use min_usd_value (backend field) or fall back to min_order_usdt (legacy)
      const minValue = alert.min_usd_value ?? alert.min_order_usdt ?? 50000
      setMinOrderUsdt([Math.max(minValue, 10000)])
      setGracePeriodSeconds([alert.grace_period_seconds ?? 120])
      setRetriggerIntervalDays([alert.retrigger_interval_days ?? 2])
      setRetriggerHourUtc([alert.retrigger_hour_utc ?? 10])
      setPriceTolerancePct([alert.price_tolerance_pct ?? 10])
      setUpdateNotificationPct(alert.update_notification_pct ?? 8)
      setMaxDeviationPct(alert.max_deviation_pct ?? 15)
      setEnabled(alert.enabled !== false)
      setIgnoreSymbols((alert.ignore || []).join(', '))
      
      // Notification settings - Detection
      setEnableTelegram(alert.enable_telegram !== false)
      setEnablePushover(alert.enable_pushover === true)
      setTelegramChatId(alert.telegram?.chat_id || '')
      setTelegramReplyTo(String(alert.telegram?.reply_to_message_id ?? ''))
      setPushoverGroupKey(alert.pushover?.group_key || '')
      setPushoverNotifyUsers((alert.pushover?.notify_users || []).join(', '))
      // Notification settings - Removal
      setEnableRemovalNotifications(alert.enable_removal_notifications !== false)
      setRemovalTelegramEnabled(alert.removal_telegram?.enabled !== false)
      setRemovalTelegramChatId(alert.removal_telegram?.chat_id || '')
      setRemovalTelegramReplyTo(String(alert.removal_telegram?.reply_to_message_id ?? ''))
      setRemovalPushoverEnabled(alert.removal_pushover?.enabled === true)
      setRemovalPushoverGroupKey(alert.removal_pushover?.group_key || '')
      setRemovalPushoverNotifyUsers((alert.removal_pushover?.notify_users || []).join(', '))
      // Notification settings - Retrigger
      setEnableRetriggerNotifications(alert.enable_retrigger_notifications !== false)
      setRetriggerTelegramEnabled(alert.retrigger_telegram?.enabled !== false)
      setRetriggerTelegramChatId(alert.retrigger_telegram?.chat_id || '')
      setRetriggerTelegramReplyTo(String(alert.retrigger_telegram?.reply_to_message_id ?? ''))
      setRetriggerPushoverEnabled(alert.retrigger_pushover?.enabled === true)
      setRetriggerPushoverGroupKey(alert.retrigger_pushover?.group_key || '')
      setRetriggerPushoverNotifyUsers((alert.retrigger_pushover?.notify_users || []).join(', '))
      // Per-exchange notification overrides
      const exNotifs: Record<string, { enabled: boolean; telegramChatId: string; telegramReplyTo: string; pushoverGroupKey: string; pushoverNotifyUsers: string }> = {}
      if (alert.exchange_notifications) {
        for (const [ex, cfg] of Object.entries(alert.exchange_notifications)) {
          const hasOverride = cfg.enable_telegram !== undefined || cfg.enable_pushover !== undefined || 
            cfg.telegram?.chat_id || cfg.telegram?.reply_to_message_id ||
            cfg.pushover?.group_key || cfg.pushover?.notify_users?.length
          exNotifs[ex] = {
            enabled: Boolean(hasOverride),
            telegramChatId: cfg.telegram?.chat_id || '',
            telegramReplyTo: String(cfg.telegram?.reply_to_message_id ?? ''),
            pushoverGroupKey: cfg.pushover?.group_key || '',
            pushoverNotifyUsers: (cfg.pushover?.notify_users || []).join(', '),
          }
        }
      }
      setExchangeNotifications(exNotifs)
      setExpandedExchanges(new Set())
      
      // Use tag_expression if available, otherwise convert from legacy
      const existingExpression = (alert as unknown as Record<string, unknown>).tag_expression as string || ''
      if (existingExpression) {
        setTagExpression(existingExpression)
        setLegacyRequired([])
        setLegacyExcluded([])
      } else {
        const required = alert.required_tags || []
        const excluded = alert.exclude_tags || []
        setLegacyRequired(required)
        setLegacyExcluded(excluded)
        if (required.length > 0 || excluded.length > 0) {
          setTagExpression(legacyTagsToExpression(required, [], excluded))
        } else {
          setTagExpression('')
        }
      }
    } else {
      setName('')
      setExchanges([])
      setMarketType('futures')
      setPrecision('0.01')
      setMinOrderUsdt([50000])
      setGracePeriodSeconds([120])
      setRetriggerIntervalDays([2])
      setRetriggerHourUtc([10])
      setPriceTolerancePct([10])
      setUpdateNotificationPct(8)
      setMaxDeviationPct(15)
      setEnabled(true)
      setIgnoreSymbols('')
      setTagExpression('')
      setLegacyRequired([])
      setLegacyExcluded([])
      // Reset notification settings
      setEnableTelegram(true)
      setEnablePushover(false)
      setTelegramChatId('')
      setTelegramReplyTo('')
      setPushoverGroupKey('')
      setPushoverNotifyUsers('')
      setEnableRemovalNotifications(true)
      setRemovalTelegramEnabled(true)
      setRemovalTelegramChatId('')
      setRemovalTelegramReplyTo('')
      setRemovalPushoverEnabled(false)
      setRemovalPushoverGroupKey('')
      setRemovalPushoverNotifyUsers('')
      setEnableRetriggerNotifications(true)
      setRetriggerTelegramEnabled(true)
      setRetriggerTelegramChatId('')
      setRetriggerTelegramReplyTo('')
      setRetriggerPushoverEnabled(false)
      setRetriggerPushoverGroupKey('')
      setRetriggerPushoverNotifyUsers('')
      setExchangeNotifications({})
      setExpandedExchanges(new Set())
    }
  }, [alert, open])

  const toggleExchange = (exchange: string) => {
    setExchanges((prev) =>
      prev.includes(exchange)
        ? prev.filter((e) => e !== exchange)
        : [...prev, exchange]
    )
  }

  const parseSymbols = (str: string): string[] => {
    return str
      .split(/[,\n]/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length > 0)
  }

  const parseTags = (str: string): string[] => {
    return str
      .split(/[,\n]/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0)
  }

  const handleSave = async () => {
    if (!name.trim() || exchanges.length === 0 || unsupportedExchanges.length > 0) return

    setIsSaving(true)
    try {
      const alertData: Partial<OrderbookAlert> & { tag_expression?: string } = {
        ...(alert?.id && { id: alert.id }),
        name: name.trim(),
        exchanges,
        market_type: marketType,
        precision,
        min_usd_value: minOrderUsdt[0],
        grace_period_seconds: gracePeriodSeconds[0],
        retrigger_interval_days: retriggerIntervalDays[0],
        retrigger_hour_utc: retriggerHourUtc[0],
        price_tolerance_pct: priceTolerancePct[0],
        update_notification_pct: updateNotificationPct,
        max_deviation_pct: maxDeviationPct,
        enabled,
        ignore: parseSymbols(ignoreSymbols),
        required_tags: [],
        exclude_tags: [],
        tag_expression: tagExpression,
        // Detection notifications
        enable_telegram: enableTelegram,
        enable_pushover: enablePushover,
        telegram: {
          chat_id: telegramChatId || undefined,
          reply_to_message_id: telegramReplyTo ? parseInt(telegramReplyTo, 10) || telegramReplyTo : undefined,
        },
        pushover: {
          group_key: pushoverGroupKey || undefined,
          notify_users: pushoverNotifyUsers ? parseSymbols(pushoverNotifyUsers).map(s => s.toLowerCase()) : undefined,
        },
        // Removal notifications
        enable_removal_notifications: enableRemovalNotifications,
        removal_telegram: {
          enabled: removalTelegramEnabled,
          chat_id: removalTelegramChatId || undefined,
          reply_to_message_id: removalTelegramReplyTo ? parseInt(removalTelegramReplyTo, 10) || removalTelegramReplyTo : undefined,
        },
        removal_pushover: {
          enabled: removalPushoverEnabled,
          group_key: removalPushoverGroupKey || undefined,
          notify_users: removalPushoverNotifyUsers ? parseSymbols(removalPushoverNotifyUsers).map(s => s.toLowerCase()) : undefined,
        },
        // Retrigger notifications
        enable_retrigger_notifications: enableRetriggerNotifications,
        retrigger_telegram: {
          enabled: retriggerTelegramEnabled,
          chat_id: retriggerTelegramChatId || undefined,
          reply_to_message_id: retriggerTelegramReplyTo ? parseInt(retriggerTelegramReplyTo, 10) || retriggerTelegramReplyTo : undefined,
        },
        retrigger_pushover: {
          enabled: retriggerPushoverEnabled,
          group_key: retriggerPushoverGroupKey || undefined,
          notify_users: retriggerPushoverNotifyUsers ? parseSymbols(retriggerPushoverNotifyUsers).map(s => s.toLowerCase()) : undefined,
        },
        // Per-exchange notification overrides
        exchange_notifications: Object.fromEntries(
          Object.entries(exchangeNotifications)
            .filter(([, cfg]) => cfg.enabled)
            .map(([ex, cfg]) => [ex, {
              telegram: {
                chat_id: cfg.telegramChatId || undefined,
                reply_to_message_id: cfg.telegramReplyTo ? parseInt(cfg.telegramReplyTo, 10) || cfg.telegramReplyTo : undefined,
              },
              pushover: {
                group_key: cfg.pushoverGroupKey || undefined,
                notify_users: cfg.pushoverNotifyUsers ? parseSymbols(cfg.pushoverNotifyUsers).map(s => s.toLowerCase()) : undefined,
              },
            }])
        ),
      }
      await onSave(alertData)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save alert:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!alert?.id || !onDelete) return
    if (!confirm('Are you sure you want to delete this alert?')) return

    setIsDeleting(true)
    try {
      await onDelete(alert.id)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete alert:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`
    return `$${value}`
  }

  const handleMinOrderClick = () => {
    setMinOrderInputValue(String(minOrderUsdt[0]))
    setIsEditingMinOrder(true)
  }

  const handleMinOrderInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers
    const value = e.target.value.replace(/[^0-9]/g, '')
    setMinOrderInputValue(value)
  }

  const handleMinOrderInputBlur = () => {
    const value = parseInt(minOrderInputValue, 10)
    if (!isNaN(value) && value >= 0) {
      // Minimum of 10000 to ensure alerts actually track orders
      const clampedValue = Math.max(10000, value)
      setMinOrderUsdt([clampedValue])
    }
    setIsEditingMinOrder(false)
  }

  const handleMinOrderInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleMinOrderInputBlur()
    } else if (e.key === 'Escape') {
      setIsEditingMinOrder(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Create Orderbook Alert' : 'Edit Orderbook Alert'}</SheetTitle>
          <SheetDescription>
            Configure orderbook monitoring for large orders.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alert-name">Alert Name</Label>
              <Input
                id="alert-name"
                placeholder="Orderbook Alert"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="alert-enabled">Enabled</Label>
              <Switch
                id="alert-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <Separator />

          <OrderbookExchangeSelector
            exchanges={exchanges}
            marketType={marketType}
            onToggle={toggleExchange}
          />

          <Separator />

          {/* Market Type & Precision */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Market Type</Label>
              <Select value={marketType} onValueChange={(v) => setMarketType(v as 'spot' | 'futures')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spot">Spot</SelectItem>
                  <SelectItem value="futures">Futures</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Precision</Label>
              <Select value={precision} onValueChange={setPrecision}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="9">Fine (9)</SelectItem>
                  <SelectItem value="0.001">0.001</SelectItem>
                  <SelectItem value="0.01">0.01</SelectItem>
                  <SelectItem value="0.1">0.1</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Minimum Order Size</Label>
              {isEditingMinOrder ? (
                <Input
                  type="text"
                  value={minOrderInputValue}
                  onChange={handleMinOrderInputChange}
                  onBlur={handleMinOrderInputBlur}
                  onKeyDown={handleMinOrderInputKeyDown}
                  className="w-28 h-7 text-sm text-right"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={handleMinOrderClick}
                  className="text-sm font-medium hover:text-primary hover:underline cursor-pointer transition-colors"
                >
                  {formatCurrency(minOrderUsdt[0])}
                </button>
              )}
            </div>
            <Slider
              value={minOrderUsdt}
              onValueChange={setMinOrderUsdt}
              min={10000}
              max={1000000}
              step={10000}
            />
            <p className="text-xs text-muted-foreground">
              Only track orders larger than this value (click value to type directly, min: $10K)
            </p>
          </div>

          <Separator />

          {/* Timing Settings */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Timing Settings</Label>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Grace Period</Label>
                <span className="text-sm font-medium">{gracePeriodSeconds[0]}s</span>
              </div>
              <Slider
                value={gracePeriodSeconds}
                onValueChange={setGracePeriodSeconds}
                min={30}
                max={600}
                step={30}
              />
              <p className="text-xs text-muted-foreground">
                How long an order must persist before alerting (anti-spoof)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Retrigger Days</Label>
                  <span className="text-sm font-medium">{retriggerIntervalDays[0]}d</span>
                </div>
                <Slider
                  value={retriggerIntervalDays}
                  onValueChange={setRetriggerIntervalDays}
                  min={1}
                  max={14}
                  step={1}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-muted-foreground">Retrigger Hour (UTC)</Label>
                  <span className="text-sm font-medium">{retriggerHourUtc[0]}:00</span>
                </div>
                <Slider
                  value={retriggerHourUtc}
                  onValueChange={setRetriggerHourUtc}
                  min={0}
                  max={23}
                  step={1}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Threshold Settings */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Threshold Settings</Label>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Price Tolerance</Label>
                <span className="text-sm font-medium">{priceTolerancePct[0]}%</span>
              </div>
              <Slider
                value={priceTolerancePct}
                onValueChange={setPriceTolerancePct}
                min={1}
                max={30}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                How far from current price an order can be to still be tracked
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Update Notification</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={updateNotificationPct !== null}
                    onCheckedChange={(checked) => setUpdateNotificationPct(checked ? 8 : null)}
                  />
                  {updateNotificationPct !== null && (
                    <span className="text-sm font-medium">{updateNotificationPct}%</span>
                  )}
                </div>
              </div>
              {updateNotificationPct !== null && (
                <Slider
                  value={[updateNotificationPct]}
                  onValueChange={(v) => setUpdateNotificationPct(v[0])}
                  min={1}
                  max={30}
                  step={1}
                />
              )}
              <p className="text-xs text-muted-foreground">
                Notify when order size changes by this percentage
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Max Deviation</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={maxDeviationPct !== null}
                    onCheckedChange={(checked) => setMaxDeviationPct(checked ? 15 : null)}
                  />
                  {maxDeviationPct !== null && (
                    <span className="text-sm font-medium">{maxDeviationPct}%</span>
                  )}
                </div>
              </div>
              {maxDeviationPct !== null && (
                <Slider
                  value={[maxDeviationPct]}
                  onValueChange={(v) => setMaxDeviationPct(v[0])}
                  min={5}
                  max={50}
                  step={1}
                />
              )}
              <p className="text-xs text-muted-foreground">
                Remove order if price deviates more than this from entry
              </p>
            </div>
          </div>

          <Separator />

          {/* Notification Settings */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Notification Settings</Label>
            
            {/* Detection Notifications */}
            <div className="space-y-3 rounded-lg border p-3">
              <Label className="text-sm text-muted-foreground">Detection Notifications</Label>
              
              <div className="flex items-center justify-between">
                <Label className="text-sm">Telegram</Label>
                <Switch checked={enableTelegram} onCheckedChange={setEnableTelegram} />
              </div>
              {enableTelegram && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Destination</Label>
                  <TelegramDestinationSelect
                    chatId={telegramChatId}
                    replyToMessageId={telegramReplyTo}
                    onChange={({ chat_id, reply_to_message_id }) => {
                      setTelegramChatId(chat_id)
                      setTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                    }}
                    triggerClassName="h-8 text-sm"
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label className="text-sm">Pushover</Label>
                <Switch checked={enablePushover} onCheckedChange={setEnablePushover} />
              </div>
              {enablePushover && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Group Key</Label>
                    <Input
                      placeholder="Group key (for group notifications)"
                      value={pushoverGroupKey}
                      onChange={(e) => setPushoverGroupKey(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Notify Users</Label>
                    <Input
                      placeholder="user1, user2 (comma-separated)"
                      value={pushoverNotifyUsers}
                      onChange={(e) => setPushoverNotifyUsers(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Removal Notifications */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Removal Notifications</Label>
                <Switch checked={enableRemovalNotifications} onCheckedChange={setEnableRemovalNotifications} />
              </div>
              
              {enableRemovalNotifications && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Telegram</Label>
                    <Switch checked={removalTelegramEnabled} onCheckedChange={setRemovalTelegramEnabled} />
                  </div>
                  {removalTelegramEnabled && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Destination</Label>
                      <TelegramDestinationSelect
                        chatId={removalTelegramChatId}
                        replyToMessageId={removalTelegramReplyTo}
                        onChange={({ chat_id, reply_to_message_id }) => {
                          setRemovalTelegramChatId(chat_id)
                          setRemovalTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                        }}
                        triggerClassName="h-8 text-sm"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Pushover</Label>
                    <Switch checked={removalPushoverEnabled} onCheckedChange={setRemovalPushoverEnabled} />
                  </div>
                  {removalPushoverEnabled && (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Group Key</Label>
                        <Input
                          placeholder="Same as detection"
                          value={removalPushoverGroupKey}
                          onChange={(e) => setRemovalPushoverGroupKey(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Notify Users</Label>
                        <Input
                          placeholder="Same as detection"
                          value={removalPushoverNotifyUsers}
                          onChange={(e) => setRemovalPushoverNotifyUsers(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Retrigger Notifications */}
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Retrigger Notifications</Label>
                <Switch checked={enableRetriggerNotifications} onCheckedChange={setEnableRetriggerNotifications} />
              </div>
              
              {enableRetriggerNotifications && (
                <>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Telegram</Label>
                    <Switch checked={retriggerTelegramEnabled} onCheckedChange={setRetriggerTelegramEnabled} />
                  </div>
                  {retriggerTelegramEnabled && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Destination</Label>
                      <TelegramDestinationSelect
                        chatId={retriggerTelegramChatId}
                        replyToMessageId={retriggerTelegramReplyTo}
                        onChange={({ chat_id, reply_to_message_id }) => {
                          setRetriggerTelegramChatId(chat_id)
                          setRetriggerTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                        }}
                        triggerClassName="h-8 text-sm"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Pushover</Label>
                    <Switch checked={retriggerPushoverEnabled} onCheckedChange={setRetriggerPushoverEnabled} />
                  </div>
                  {retriggerPushoverEnabled && (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Group Key</Label>
                        <Input
                          placeholder="Same as detection"
                          value={retriggerPushoverGroupKey}
                          onChange={(e) => setRetriggerPushoverGroupKey(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Notify Users</Label>
                        <Input
                          placeholder="Same as detection"
                          value={retriggerPushoverNotifyUsers}
                          onChange={(e) => setRetriggerPushoverNotifyUsers(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Per-Exchange Notification Overrides */}
            {exchanges.length > 0 && (
              <div className="space-y-2 rounded-lg border p-3">
                <Label className="text-sm text-muted-foreground">Per-Exchange Overrides</Label>
                <p className="text-xs text-muted-foreground">
                  Optional: Override notification settings for specific exchanges
                </p>
                {exchanges.map((exchange) => {
                  const exConfig = exchangeNotifications[exchange] || {
                    enabled: false,
                    telegramChatId: '',
                    telegramReplyTo: '',
                    pushoverGroupKey: '',
                    pushoverNotifyUsers: '',
                  }
                  const isExpanded = expandedExchanges.has(exchange)

                  return (
                    <Collapsible
                      key={exchange}
                      open={isExpanded}
                      onOpenChange={(open) => {
                        setExpandedExchanges((prev) => {
                          const next = new Set(prev)
                          if (open) next.add(exchange)
                          else next.delete(exchange)
                          return next
                        })
                      }}
                    >
                      <div className="flex items-center gap-2 py-1">
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </CollapsibleTrigger>
                        <span className="text-sm font-medium capitalize">{exchange}</span>
                        <Switch
                          checked={exConfig.enabled}
                          onCheckedChange={(checked) => {
                            setExchangeNotifications((prev) => ({
                              ...prev,
                              [exchange]: { ...exConfig, enabled: checked },
                            }))
                          }}
                        />
                        {exConfig.enabled && <Badge variant="outline" className="text-xs">Override</Badge>}
                      </div>
                      <CollapsibleContent className="pl-8 space-y-2">
                        {exConfig.enabled && (
                          <>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Telegram Destination</Label>
                              <TelegramDestinationSelect
                                chatId={exConfig.telegramChatId}
                                replyToMessageId={exConfig.telegramReplyTo}
                                onChange={({ chat_id, reply_to_message_id }) => {
                                  setExchangeNotifications((prev) => ({
                                    ...prev,
                                    [exchange]: {
                                      ...exConfig,
                                      telegramChatId: chat_id,
                                      telegramReplyTo: reply_to_message_id ? String(reply_to_message_id) : '',
                                    },
                                  }))
                                }}
                                triggerClassName="h-7 text-xs"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Pushover Group Key</Label>
                                <Input
                                  placeholder="Override group key"
                                  value={exConfig.pushoverGroupKey}
                                  onChange={(e) => {
                                    setExchangeNotifications((prev) => ({
                                      ...prev,
                                      [exchange]: { ...exConfig, pushoverGroupKey: e.target.value },
                                    }))
                                  }}
                                  className="h-7 text-xs"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Notify Users</Label>
                                <Input
                                  placeholder="Override users"
                                  value={exConfig.pushoverNotifyUsers}
                                  onChange={(e) => {
                                    setExchangeNotifications((prev) => ({
                                      ...prev,
                                      [exchange]: { ...exConfig, pushoverNotifyUsers: e.target.value },
                                    }))
                                  }}
                                  className="h-7 text-xs"
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )
                })}
              </div>
            )}
          </div>

          <Separator />

          {/* Tags */}
          <div className="space-y-4">
            <TagExpressionInput
              value={tagExpression}
              onChange={setTagExpression}
              legacyRequired={legacyRequired}
              legacyExcluded={legacyExcluded}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="ignore-symbols">Ignore Symbols</Label>
            <Input
              id="ignore-symbols"
              placeholder="BTC, ETH"
              value={ignoreSymbols}
              onChange={(e) => setIgnoreSymbols(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Symbols to ignore for this alert
            </p>
          </div>
        </div>

        <SheetFooter className="gap-2 sm:gap-0">
          {!isNew && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isDeleting || !name.trim() || exchanges.length === 0 || unsupportedExchanges.length > 0}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
